import React, { useState, useEffect } from "react";
import { Tab, TabList, Button, Spinner, Text } from "@fluentui/react-components";
import { Dismiss20Regular, Save20Regular, Edit20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import { emptyIaAgentObject } from "./emptyIaAgentObject";
import IdentityTab from "./tabs/IdentityTab";
import VoiceTab from "./tabs/VoiceTab";
import PersonalityTab from "./tabs/PersonalityTab";
import GreetingTab from "./tabs/GreetingTab";
import SkillsTab from "./tabs/SkillsTab";
import RoutingTab from "./tabs/RoutingTab";
import KnowledgeBaseTab from "./tabs/KnowledgeBaseTab";
import SecurityErrorsTab from "./tabs/SecurityErrorsTab";
import {
  GetIaAgent,
  AddIaAgent,
  UpdateIaAgent,
  GetAllIaAgent,
} from "../../Services/HeedifyServices";
import { getAccessToken } from "../../Services/GraphServices";
import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from "../GenericComponent/NotificationSystem";

import "../CSS/CallFlowsComponent.css";

export default function IaAgentComponent({ st_IaAgents, setIaAgents, ACTIVE_TENANT, SCOPES }) {
  const { t } = useTranslation();
  const { iaAgentId } = useParams();
  const navigate = useNavigate();
  const ability = useAbility();
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;

  const isCreateMode = iaAgentId === "newIaAgent";
  const isEditMode = !isCreateMode;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedTab, setSelectedTab] = useState("identity");
  const [agentData, setAgentData] = useState(emptyIaAgentObject);
  const { showSuccess, showError } = useNotification();

  // Load existing agent
  useEffect(() => {
    const loadAgent = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        if (isEditMode) {
          const token = await getAccessToken(instance, activeAccount);
          const existing = st_IaAgents?.find((a) => a.id === iaAgentId);
          const scope = existing?.scope || "default";
          const response = await GetIaAgent(ACTIVE_TENANT, userId, iaAgentId, scope, token);
          if (response) {
            setAgentData({ ...emptyIaAgentObject, ...response });
          } else {
            setLoadFailed(true);
          }
        } else {
          setAgentData({ ...emptyIaAgentObject });
        }
      } catch (error) {
        console.error("Error loading ia agent:", error);
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    };
    loadAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, iaAgentId]);

  const refreshAll = async (token) => {
    try {
      const scopeNames = Array.isArray(SCOPES) ? SCOPES : SCOPES ? Object.keys(SCOPES) : ["default"];
      const results = await Promise.all(
        scopeNames.map((scope) => GetAllIaAgent(ACTIVE_TENANT, userId, scope, token))
      );
      const all = results.flat();
      const unique = Array.from(new Map(all.map((a) => [a.id, a])).values());
      setIaAgents(unique);
    } catch (err) {
      console.error("Error refreshing ia agents:", err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      const payload = { ...agentData };

      if (isCreateMode) {
        const result = await AddIaAgent(ACTIVE_TENANT, userId, agentData.scope || "default", token, payload);
        if (result?.success === true || result?.message === "Created") {
          await refreshAll(token);
          showSuccess(t("IA_AGENT_CREATED_SUCCESS"));
          navigate("/iaAgent");
        } else {
          showError(t("IA_AGENT_CREATION_FAILED"), result?.message || "Unknown error");
        }
      } else {
        const result = await UpdateIaAgent(
          ACTIVE_TENANT,
          userId,
          agentData.id,
          agentData.scope || "default",
          token,
          payload
        );
        if (result?.success === true || result?.message === "Updated") {
          await refreshAll(token);
          showSuccess(t("IA_AGENT_UPDATED_SUCCESS"));
          navigate("/iaAgent");
        } else {
          showError(t("IA_AGENT_UPDATE_FAILED"), result?.message || "Unknown error");
        }
      }
    } catch (err) {
      console.error("Error saving ia agent:", err);
      showError(isCreateMode ? t("IA_AGENT_CREATION_ERROR") : t("IA_AGENT_UPDATE_ERROR"), err?.message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    // Simple setter supporting nested paths like "agentPersonality.name"
    setAgentData((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cursor = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cursor[parts[i]] = { ...(cursor[parts[i]] || {}) };
        cursor = cursor[parts[i]];
      }
      cursor[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="tab-loading">
          <Spinner label={t("LOADING")} size="large" />
        </div>
      );
    }
    if (loadFailed) {
      return (
        <div className="tab-error">
          <Text weight="semibold">{t("ERROR_LOADING_IA_AGENT")}</Text>
        </div>
      );
    }

    const commonProps = { agentData, updateField, isCreateMode };

    switch (selectedTab) {
      case "identity":
        return <IdentityTab {...commonProps} />;
      case "voice":
        return <VoiceTab {...commonProps} />;
      case "personality":
        return <PersonalityTab {...commonProps} />;
      case "greeting":
        return <GreetingTab {...commonProps} />;
      case "skills":
        return <SkillsTab {...commonProps} />;
      case "routing":
        return <RoutingTab {...commonProps} />;
      case "knowledgeBase":
        return <KnowledgeBaseTab {...commonProps} />;
      case "security":
        return <SecurityErrorsTab {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="callflow-container">
      <div className="callflow-tab-header">
        <TabList selectedValue={selectedTab} onTabSelect={(_, data) => setSelectedTab(data.value)}>
          <Tab value="identity">{t("IA_AGENT_TAB_IDENTITY")}</Tab>
          {agentData?.id && (
            <>
              <Tab value="voice">{t("IA_AGENT_TAB_VOICE")}</Tab>
              <Tab value="personality">{t("IA_AGENT_TAB_PERSONALITY")}</Tab>
              <Tab value="greeting">{t("IA_AGENT_TAB_GREETING")}</Tab>
              <Tab value="skills">{t("IA_AGENT_TAB_SKILLS")}</Tab>
              <Tab value="routing">{t("IA_AGENT_TAB_ROUTING")}</Tab>
              <Tab value="knowledgeBase">{t("IA_AGENT_TAB_KB")}</Tab>
              <Tab value="security">{t("IA_AGENT_TAB_SECURITY")}</Tab>
            </>
          )}
        </TabList>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 10px 0" }}>
          <div style={{ flex: 1 }}></div>
          {agentData?.displayName && (
            <p className="callflow-display-name" style={{ flex: 1, textAlign: "center", margin: 0 }}>
              {agentData.displayName}
            </p>
          )}
          <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <Button icon={<Dismiss20Regular />} appearance="secondary" onClick={() => navigate("/iaAgent")}>
              {t("CANCEL")}
            </Button>
            <Button
              icon={saving ? <Spinner size="tiny" /> : isEditMode ? <Edit20Regular /> : <Save20Regular />}
              appearance="primary"
              className="btn-save-style"
              onClick={handleSave}
              disabled={saving || (isCreateMode && (!agentData?.id || !agentData?.displayName))}
            >
              {t("SAVE")}
            </Button>
          </div>
        </div>
      </div>

      <div className="callflow-tab-content">{renderTabContent()}</div>
    </div>
  );
}
